module Shared.IllyAdmin.Loggers

open Illy.SafeStack

type LoggerMetadata =
    {
        DisplayName: string
        Description: string
        Application: string
        Key: string
    }

module LoggerMetadata =
    let toKey m =
        m.Key

    let empty =
        {
            DisplayName = ""
            Description = ""
            Application = ""
            Key = ""
        }

[<RequireQualifiedAccess>]
type Severity =
    | All
    | Debug
    | Info
    | Error

[<RequireQualifiedAccess>]
type OutputMode =
    | Database
    | File of string option

module OutputMode =
    let toInt =
        function
        | OutputMode.Database -> 0
        | OutputMode.File _ -> 1

    let ofInt =
        function
        | 0 -> OutputMode.Database
        | 1 -> OutputMode.File None
        | x -> failwithf "Could not convert %d to %s" x typeof<OutputMode>.FullName

    let toDisplayName =
        function
        | OutputMode.Database -> "Database"
        | OutputMode.File _ -> "File"

    let toListOption x = { Id = toInt x; Value = toDisplayName x; Group = "" }

module Severity =
    let toInt =
        function
        | Severity.All -> 0
        | Severity.Debug -> 1
        | Severity.Info -> 2
        | Severity.Error -> 3

    let ofInt =
        function
        | 0 -> Severity.All
        | 1 -> Severity.Debug
        | 2 -> Severity.Info
        | 3 -> Severity.Error
        | x -> failwithf "Could not convert %d to %s" x typeof<Severity>.FullName

    let toDisplayName =
        function
        | Severity.All -> "All"
        | Severity.Debug -> "Debug"
        | Severity.Info -> "Info"
        | Severity.Error -> "Error"

    let toListOption x = { Id = toInt x; Value = toDisplayName x; Group = "" }

[<RequireQualifiedAccess>]
type PresentationMethod =
    | Download
    | OnScreen

module PresentationMethod =
    let toInt =
        function
        | PresentationMethod.Download -> 0
        | PresentationMethod.OnScreen -> 1

    let ofInt =
        function
        | 0 -> PresentationMethod.Download
        | 1 -> PresentationMethod.OnScreen
        | x -> failwithf "Could not convert %d to %s" x typeof<PresentationMethod>.FullName

    let toDisplayName =
        function
        | PresentationMethod.Download -> "Download"
        | PresentationMethod.OnScreen -> "Preview on Screen"

    let toListOption x = { Id = toInt x; Value = toDisplayName x; Group = "" }

type LoggerConfigDTO =
    {
        Key: string
        Output: OutputMode
        Severity: Severity
    }

type LoggersDTO =
    {
        DefaultFilepath: string option
        Loggers: LoggerConfigDTO list
    }

module Validations =
    type LoggerKey =
        | LoggerKey of string

    module LoggerKey =
        let unwrap =
            function | LoggerKey s -> s

    type FilepathError =
        | MustBeginWithADriveLetter
        | MustContainNoRelativeComponent
        | MustBeSetIfOutputDirectoryIsBlank
        | MustIncludeAFileName
        | MustNotBeLongerThan400Characters
        | MustNotContainForbiddenCharacters
        | MustUseCorrectSlash
        | MustContainNoRepeatedSlashes

    module FilepathError =
        let toString =
            function
            | MustBeginWithADriveLetter -> "The filepath must begin with a drive letter (eg. C:\)."
            | MustContainNoRelativeComponent -> "The filepath must contain no relative components (eg. \..)."
            | MustBeSetIfOutputDirectoryIsBlank -> "The filepath must be set if the default output directory is not set."
            | MustIncludeAFileName -> "The filepath must include a file name."
            | MustNotBeLongerThan400Characters -> "The filepath must not be longer than 400 characters."
            | MustNotContainForbiddenCharacters -> "The filepath must not contain disallowed characters (:\\/*?<>|)."
            | MustUseCorrectSlash -> "The filepath must use backslashes (\\)."
            | MustContainNoRepeatedSlashes -> "The filepath must not have any repeated slashes (eg. \\\\)."

    type FileNameError =
        | ExtensionMustBeTxt
        | MustNotContainForbiddenCharacters
        | MustNotBeLongerThan100Characters
        | MustBeLongerThan0Characters

    module FileNameError =
        let toString =
            function
            | ExtensionMustBeTxt -> "The extension on the file must be '.txt'."
            | MustNotContainForbiddenCharacters -> "The file name must not contain disallowed characters (:\\/*?<>|)."
            | MustNotBeLongerThan100Characters -> "The file name must not be longer than 100 characters."
            | MustBeLongerThan0Characters -> "The file name must be longer than 0 characters"

    type LoggerConfigError =
        | FilepathError of FilepathError
        | FileNameError of FileNameError

    module LoggerConfigError =
        let toString =
            function
            | FilepathError e -> FilepathError.toString e
            | FileNameError e -> FileNameError.toString e

    type DefaultPathError =
        | FilepathError of FilepathError

    module DefaultPathError =
        let toString =
            function
            | FilepathError e -> FilepathError.toString e

    type LoggersError =
        | DefaultPathError of DefaultPathError
        | LoggerConfigError of LoggerKey * LoggerConfigError

    module LoggersError =
        let toString =
            function
            | DefaultPathError e -> DefaultPathError.toString e
            | LoggerConfigError (_,e) -> LoggerConfigError.toString e

type ConfigPreviewResult = ROP.Result<string, Validations.LoggersError list>
type ConfigDownloadResult = ROP.Result<string * byte array, Validations.LoggersError list>